import { memo } from 'react';
import {
  OwcTypography,
  OwcProgressBar,
  OwcTable,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableBody,
  OwcTableRow,
  OwcTableCell,
  OwcInput,
  OwcIcon,
} from '@one/react';
import Banner from '../../../../components/Common/Banner';
import StatusChip from '../../../../components/Common/StatusChip';
import ConfirmInput from '../../../../components/Common/ConfirmInput';
import Tabs from '../../../../components/Common/Tabs';
import EntityTypeChip from '../../../../components/Common/EntityTypeChip';
import EntityPath from '../../../../components/Common/EntityPath';
// import OrphanFilterToggle from '../../../../components/Common/OrphanFilterToggle'; // hidden for now
import styles from './HardDeleteReleaseModal.module.css';
import tbl from '../../../../components/Common/entityTable.module.css';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'event', label: 'Events' },
  { key: 'resource', label: 'Resources' },
  { key: 'cdt', label: 'CDTs' },
];

/**
 * Read-only entity row for the delete plan. Memoized because OWC table cells are
 * web components and expensive to re-instantiate on tab/filter switches.
 */
const EntityRow = memo(function EntityRow({ entity, releaseVersion, showType }) {
  const isDelete = entity.action === 'delete';
  const remainingReleases = entity.newReleaseField
    ? entity.newReleaseField.split('#').filter(Boolean)
    : [];
  return (
    <OwcTableRow>
      <OwcTableCell align="left" valign="middle">
        <div className={tbl.entityName}>
          <strong>{entity.name}</strong>
        </div>
      </OwcTableCell>
      <OwcTableCell align="left" valign="middle">
        <EntityPath parent={entity.parent} />
      </OwcTableCell>
      {showType && (
        <OwcTableCell align="left" valign="middle">
          <EntityTypeChip type={entity.type} />
        </OwcTableCell>
      )}
      <OwcTableCell align="left" valign="middle">
        <span className={`${tbl.badge} ${isDelete ? tbl.badgeDelete : tbl.badgeUntag}`}>
          {entity.action}
        </span>
        {isDelete ? (
          <span className={tbl.versionOld}>exclusive to Release {releaseVersion}</span>
        ) : (
          <>
            <span className={tbl.versionOld}>Release {releaseVersion}</span>
            {remainingReleases.length > 0 && (
              <span className={tbl.versionNew}>
                {' '}
                → Release {remainingReleases.join(', Release ')}
              </span>
            )}
          </>
        )}
      </OwcTableCell>
    </OwcTableRow>
  );
});

/**
 * Step 2 of the hard-delete flow: impact summary, delete-mode notice,
 * cross-release warnings, the (read-only) entity table, and the type-to-confirm
 * guard. There is no per-entity selection — the scope is the single
 * `deleteEntities` toggle chosen in step 1.
 *
 * Presentational: all state and handlers come from the orchestrator.
 */
function HardDeleteReview({
  plan,
  releaseVersion,
  deleteEntities,
  actionCounts,
  filteredEntities,
  activeTab,
  search,
  confirmVersion,
  errorMessage,
  isLoading,
  onTabChange,
  onSearchChange,
  onConfirmChange,
}) {
  const affected = plan?.affectedEntities ?? 0;
  // The Type column is only meaningful in the "All" tab; type-specific tabs
  // already scope rows to a single type.
  const showType = activeTab === 'all';
  const deleteCount = actionCounts?.delete || 0;
  const untagCount = actionCounts?.untag || 0;
  const preservedSharedCount = actionCounts?.preservedShared || 0;

  return (
    <div className={styles.reviewStep}>
      <div className={styles.summaryRow}>
        <OwcTypography variant="title5">
          Impact: {affected} entit{affected === 1 ? 'y' : 'ies'}
        </OwcTypography>
        {deleteCount > 0 && (
          <StatusChip tone="error">
            {deleteCount} permanent deletion{deleteCount === 1 ? '' : 's'}
          </StatusChip>
        )}
        {untagCount > 0 && <StatusChip tone="neutral">{untagCount} untagged</StatusChip>}
        {preservedSharedCount > 0 && (
          <StatusChip tone="warning">
            {preservedSharedCount} shared entit{preservedSharedCount === 1 ? 'y' : 'ies'} preserved
          </StatusChip>
        )}
      </div>

      <div className={`${styles.modeHint} ${styles.modeHintDanger}`}>
        {deleteEntities ? (
          <>
            <strong className={styles.danger}>
              {deleteCount} release-exclusive entit{deleteCount === 1 ? 'y' : 'ies'} will be
              permanently deleted.
            </strong>{' '}
            {preservedSharedCount > 0 && (
              <>
                <strong>
                  {preservedSharedCount} shared entit
                  {preservedSharedCount === 1 ? 'y will' : 'ies will'} remain active
                </strong>{' '}
                and be untagged from this release.{' '}
              </>
            )}
            The release record will be removed from the database and S3.{' '}
            <strong className={styles.danger}>
              This action cannot be undone — permanently deleted entities and the release cannot be
              recovered once executed.
            </strong>
          </>
        ) : (
          <>
            All entities will be <strong>untagged</strong> (kept active); the release record will be{' '}
            <strong className={styles.danger}>permanently deleted</strong> from the database and S3.{' '}
            <strong className={styles.danger}>
              This action cannot be undone — the deleted release cannot be recovered once executed.
            </strong>
          </>
        )}
      </div>

      <div className={styles.filters}>
        <Tabs tabs={TABS} active={activeTab} onChange={onTabChange} ariaLabel="Filter by type" />
        <div className={styles.filtersRight}>
          <OwcInput
            variant="outlined"
            size="small"
            rounded
            placeholder="Filter by name or parent..."
            value={search}
            aria-label="Filter entities by name or parent"
            onInput={(e) => onSearchChange(e.target.value)}
            onClearClick={() => onSearchChange('')}
            style={{ width: 220 }}
          >
            <OwcIcon slot="prefix" name="search" family="outlined" />
          </OwcInput>
        </div>
      </div>

      {filteredEntities.length === 0 ? (
        <div className={styles.emptyState}>No entities match the current filter.</div>
      ) : (
        <div className={styles.tableWrap}>
          <OwcTable size="medium">
            <OwcTableHeader>
              <OwcTableHeaderCell width="auto" align="left">
                Name
              </OwcTableHeaderCell>
              <OwcTableHeaderCell width="auto" align="left">
                Parent
              </OwcTableHeaderCell>
              {showType && (
                <OwcTableHeaderCell width="110px" align="left">
                  Type
                </OwcTableHeaderCell>
              )}
              <OwcTableHeaderCell width="200px" align="left">
                Action
              </OwcTableHeaderCell>
            </OwcTableHeader>
            <OwcTableBody>
              {filteredEntities.map((entity) => (
                <EntityRow
                  key={entity.pk}
                  entity={entity}
                  releaseVersion={releaseVersion}
                  showType={showType}
                />
              ))}
            </OwcTableBody>
          </OwcTable>
        </div>
      )}

      <ConfirmInput
        title="Confirm deletion"
        confirmText={releaseVersion}
        value={confirmVersion}
        onChange={onConfirmChange}
      />

      {errorMessage && <Banner variant="error">{errorMessage}</Banner>}

      {isLoading && (
        <div className={styles.progress}>
          <OwcProgressBar size="medium" color="error" indeterminate />
          <div className={styles.progressLabel}>Deleting release, please wait...</div>
        </div>
      )}
    </div>
  );
}

export default memo(HardDeleteReview);
